import {
  forwardRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';

// Strip native `prefix` (string-typed in HTMLAttributes) so we can replace
// it with a ReactNode slot. `suffix` is not on InputHTMLAttributes so it
// passes through.
interface InputBaseProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  readonly error?: string;
  /** Renders before the input (icon, currency symbol). */
  readonly prefix?: ReactNode;
  /** Renders after the input (icon, unit). Note: password inputs already render their own toggle on the right. */
  readonly suffix?: ReactNode;
  readonly helperText?: string;
}

/**
 * A visible `label` is only useful if it can be bound to a control, and the
 * binding below is `id ?? name`. Given neither, React drops both undefined
 * attributes: the <label> renders with no `for`, the control with no `id`, and
 * the two are never associated. A screen reader then announces an unlabelled
 * field and `getByLabelText` cannot find it — while the rendered output looks
 * completely normal. That invisibility is how 26 of these accumulated in a
 * single file (#29 / #1075).
 *
 * Requiring the binding in the type makes the broken state unwriteable rather
 * than merely noticeable — the same move `dog-fields.ts` made for Dog columns.
 * Controls with no visible label are unaffected: they pass no `label` at all.
 */
type LabelBinding =
  | { readonly label: string; readonly id: string }
  | { readonly label: string; readonly name: string }
  | { readonly label?: never };

export type InputProps = InputBaseProps & LabelBinding;

/**
 * Form text input with label, error state, password show/hide toggle, and
 * optional prefix/suffix slots. Mirrors HTHD's
 * `customer-app/src/components/ui/Input.tsx` so ports can keep the same
 * `<Input label="…" error="…" />` calls.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className = '',
      label,
      error,
      helperText,
      prefix,
      suffix,
      id,
      type,
      autoCapitalize,
      autoCorrect,
      autoComplete,
      ...props
    },
    ref,
  ) => {
    const inputId = id ?? props.name;
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === 'password';
    const effectiveType = isPassword && showPassword ? 'text' : type;

    const inputClass = [
      'ferro-field__input',
      'ferro-input',
      error ? 'ferro-input--invalid' : '',
      prefix ? 'ferro-input--has-prefix' : '',
      suffix || isPassword ? 'ferro-input--has-suffix' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className="ferro-field">
        {label ? (
          <label htmlFor={inputId} className="ferro-field__label">
            {label}
          </label>
        ) : null}
        <div className="ferro-input__wrap">
          {prefix ? <span className="ferro-input__prefix">{prefix}</span> : null}
          <input
            ref={ref}
            id={inputId}
            type={effectiveType}
            className={inputClass}
            autoCapitalize={isPassword ? 'off' : autoCapitalize}
            autoCorrect={isPassword ? 'off' : autoCorrect}
            autoComplete={isPassword ? autoComplete ?? 'current-password' : autoComplete}
            aria-invalid={error ? true : undefined}
            aria-describedby={
              error ? `${inputId ?? 'input'}-error` :
              helperText ? `${inputId ?? 'input'}-help` :
              undefined
            }
            {...props}
          />
          {isPassword ? (
            <button
              type="button"
              className="ferro-input__suffix ferro-input__toggle"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              tabIndex={-1}
            >
              {showPassword ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          ) : suffix ? (
            <span className="ferro-input__suffix">{suffix}</span>
          ) : null}
        </div>
        {error ? (
          <p id={`${inputId ?? 'input'}-error`} className="ferro-form__error">
            {error}
          </p>
        ) : helperText ? (
          <p id={`${inputId ?? 'input'}-help`} className="ferro-field__helper">
            {helperText}
          </p>
        ) : null}
      </div>
    );
  },
);

Input.displayName = 'Input';

function EyeIcon(): JSX.Element {
  return (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function EyeOffIcon(): JSX.Element {
  return (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
      />
    </svg>
  );
}
